import { Suspense, lazy } from 'react'
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'
import { NO_FEATURES, type FeatureFlags } from '@greencandle/api-client'
import { useAuth } from './auth'
import type { AuthState } from './auth'
import { Login } from './components/Login'
import { TerminalPage } from './pages/TerminalPage'

/**
 * Every route but the landing one is split out.
 *
 * <p>The bundle was 970 kB raw and 306 kB gzipped, and nearly all of the weight is ECharts -
 * imported only by the dashboard and the standalone options page, neither of which is the first
 * screen anyone sees. So the login form and the terminal were both paying to download a charting
 * library they never render.
 *
 * <p>The terminal itself stays eager. It is the landing route, and making the first screen wait
 * on a second request to start would trade one problem for a worse one.
 */
const named = <T extends string>(name: T) =>
  <M extends Record<T, React.ComponentType>>(module: M) => ({ default: module[name] })

const AccountsPage = lazy(() => import('./pages/AccountsPage').then(named('AccountsPage')))
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(named('DashboardPage')))
const HoldingsPage = lazy(() => import('./pages/HoldingsPage').then(named('HoldingsPage')))
const ImportPage = lazy(() => import('./pages/ImportPage').then(named('ImportPage')))
const BriefingPage = lazy(() => import('./pages/BriefingPage').then(named('BriefingPage')))
const OptionsPage = lazy(() => import('./pages/OptionsPage').then(named('OptionsPage')))
const TransactionsPage = lazy(() =>
  import('./pages/TransactionsPage').then(named('TransactionsPage')))
const WizardPage = lazy(() => import('./pages/WizardPage').then(named('WizardPage')))

interface RouterContext {
  auth: AuthState
}

/**
 * A route whose feature is off behaves as if it were not registered.
 *
 * <p>Exported and free-standing so it can be tested without standing up a router - nothing in
 * this suite does, and a one-line guard does not justify being the first thing that has to.
 *
 * <p>There is no "not decided yet" case to handle: `main.tsx` does not render `RouterProvider`
 * at all until the session check has resolved, so by the time a reader can reach any of these
 * the flags are already here. `PENDING_AUTH` below covers only the router's synchronous first
 * match at module load, and it carries `NO_FEATURES` for the same reason its user is null.
 *
 * <p>A redirect rather than a not-found: this app defines no not-found component, so the
 * built-in one would be the single unstyled screen in it. The way in is gone from the
 * navigation anyway - only a stale bookmark arrives here.
 */
export function requireFeature(features: FeatureFlags, key: keyof FeatureFlags): void {
  if (!features[key]) {
    throw redirect({ to: '/' })
  }
}

function Shell() {
  const { user, signOut } = useAuth()

  return (
    <div className="shell">
      <header className="topbar">
        {/* The brand is the only way back to the terminal now that the page nav is gone, so it
            is a link rather than a label - otherwise /transactions is a dead end. */}
        <Link to="/" className="brand">
          <span className="brand-mark" aria-hidden="true" />
          Green Candle
        </Link>

        <div className="topbar-end">
          <span className="muted">{user?.username}</span>
          <button type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>

      <main className="page">
        {/* A split route arrives a moment later; the fallback says so rather than blanking.
            Deliberately plain - a skeleton that guesses the shape of a page it has not loaded
            is a guess the reader has to unlearn when the real one lands. */}
        <Suspense fallback={<p className="muted">Loading…</p>}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}

/**
 * A render fault used to mean a blank page - nothing wrapped the tree, so an uncaught error in
 * any route unmounted React entirely and left whatever was in the DOM when it crashed. This is
 * every route's fallback unless a route defines its own, since it is set once here on the root
 * and errorComponent inherits down the tree.
 *
 * <p>`reset` retries the same route rather than reloading the page - most render faults here are
 * a null the code did not expect from a still-loading query, not a real defect, and the query
 * that returned it may already have what it needs by the time this is read.
 */
function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  return (
    <div className="centered">
      <div className="card" style={{ maxWidth: 420 }}>
        <div className="brand" style={{ marginBottom: 20 }}>
          <span className="brand-mark" aria-hidden="true" />
          Green Candle
        </div>
        <div className="notice error" role="alert" style={{ marginBottom: 14 }}>
          This screen hit a problem rendering.
        </div>
        <p className="muted" style={{ marginBottom: 14 }}>
          {error instanceof Error ? error.message : 'An unexpected error occurred.'}
        </p>
        <button className="primary" type="button" onClick={reset} style={{ width: '100%' }}>
          Try again
        </button>
      </div>
    </div>
  )
}

// Pure pass-through: carries the auth context and the app-wide error boundary, but renders no
// chrome of its own. /login sits directly beneath it, unguarded and without the authenticated
// topbar; every other route sits beneath protectedRoute instead.
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  errorComponent: RouteError,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>) => ({
    // Where to land after signing in - set by protectedRoute's beforeLoad when it redirects a
    // deep link here, so a bookmarked /transactions still ends up at /transactions afterward
    // rather than always dropping back to the terminal.
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  // The return trip, and deliberately not driven by a `navigate()` call from the login form
  // itself. Calling `navigate()` immediately after `signIn()` resolves races the same ordering
  // problem the placeholder context did above: the router's `context.auth` only reflects the
  // new user once React has re-rendered and `RouterProvider` has pushed it through, which has
  // not necessarily happened yet at the point `signIn`'s promise resolves - a `navigate()` right
  // there would run this route's neighbour's `beforeLoad` against the *old* `context.auth.user`
  // and bounce straight back. Routing the return trip through `beforeLoad` here instead means it
  // rides the same `router.invalidate()` that `AuthProvider`'s `useEffect` already fires only
  // after that render has actually committed (auth.tsx) - so by the time this runs, the context
  // is guaranteed current.
  beforeLoad: ({ context, search }) => {
    if (context.auth.user) {
      throw redirect({ to: search.redirect || '/' })
    }
  },
  component: Login,
})

/**
 * The layout every real screen sits under. Route-level rather than the app-root switch this
 * used to be (`main.tsx` rendering `<Login/>` instead of the router entirely): `beforeLoad` is
 * the mechanism the router itself understands, which is what lets a signed-out visit to
 * `/transactions` come back to `/transactions` after signing in instead of always landing on
 * the terminal, and what keeps `/login` a real, bookmarkable, back-button-navigable URL instead
 * of an invisible state swap that leaves the address bar showing whatever page it happened to
 * be showing when the session ended.
 */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: ({ context, location }) => {
    if (!context.auth.user) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: Shell,
})

/**
 * The terminal is the landing screen: it is the multi-asset view the whole portfolio is read
 * from. The per-account dashboard keeps its charts and moves to its own path rather than being
 * removed - it is still the only place the equity curve and the benchmark comparison are drawn.
 */
const terminalRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: TerminalPage,
})

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/dashboard',
  component: DashboardPage,
})

const accountsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/accounts',
  component: AccountsPage,
})

const holdingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/holdings',
  component: HoldingsPage,
})

const optionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/options',
  component: OptionsPage,
})

const transactionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/transactions',
  component: TransactionsPage,
})

const importRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/import',
  component: ImportPage,
})

/**
 * The audit. Its own route rather than a terminal tab: it is a sitting rather than a glance, it
 * has server-side state of its own, and a reader part-way through one should be able to send
 * themselves the link and pick it up on another device.
 */
/**
 * FR-K9. «Разбор». Its own route, like the audit: a page read after every statement, with state
 * of its own (the reader's profile and themes), and linked from the equity desk it measures.
 */
const briefingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/briefing',
  beforeLoad: ({ context }) => requireFeature(context.auth.features, 'briefing'),
  component: BriefingPage,
})

const wizardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/wizard',
  component: WizardPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    terminalRoute,
    dashboardRoute,
    holdingsRoute,
    optionsRoute,
    transactionsRoute,
    wizardRoute,
    briefingRoute,
    accountsRoute,
    importRoute,
  ]),
])

// The router matches the current URL - and runs `protectedRoute`'s `beforeLoad` - the moment
// it is constructed, which is before `AuthProvider` has ever resolved and before
// `RouterProvider` has ever mounted to supply the real context via its `context` prop
// (main.tsx). A bare `undefined` placeholder here crashed that first pass with `Cannot read
// properties of undefined (reading 'user')`, caught by the error boundary rather than by the
// guard. A real, safe placeholder - signed-out shaped - makes that first pass redirect to
// `/login` instead of throwing; the `useEffect` in `AuthProvider` keyed on `user` calls
// `router.invalidate()` the moment the real check resolves, which corrects that redirect for
// an actually-signed-in visitor within one more pass.
const PENDING_AUTH: AuthState = {
  user: null,
  features: NO_FEATURES,
  loading: true,
  signIn: () => Promise.reject(new Error('Auth is not ready yet')),
  signOut: () => Promise.reject(new Error('Auth is not ready yet')),
}

export const router = createRouter({
  routeTree,
  context: { auth: PENDING_AUTH },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
